import { ESdChangeReason, IScreenDesign } from "@kuika/kuika-cl-model";
import rfdc from "rfdc";

const cloneDeep = rfdc();

export class UndoableScreenDesign {
  static undoable = () => {
    const maxUndoCount: number = 50;
    const defaultPast: any[] = [];
    const defaultPresent: any = {};
    const defaultFuture: any[] = [];
    // `past` is capped, so its length stops growing after maxUndoCount edits and cannot say how many
    // entries an operation pushed. `appendCount` counts every local entry ever pushed (uncapped) and
    // `revision` every state change of any kind, so a caller can measure "entries this operation added"
    // and "has anything happened since" — see getUndoHistoryMarker in screen-provider.
    const initialState = {
      past: defaultPast,
      present: defaultPresent,
      future: defaultFuture,
      appendCount: 0,
      revision: 0
    };

    return (state = initialState, action) => {
      const { past, present, future } = state;
      const appendCount: number = state.appendCount ?? 0;
      const revision: number = (state.revision ?? 0) + 1;

      switch (action.type) {
        case "UNDO":
          return {
            past: past.slice(0, past.length - 1),
            present: cloneDeep(past[past.length - 2]),
            future: [cloneDeep(present), ...future],
            appendCount,
            revision
          };
        case "REDO":
          return {
            past: [...past, cloneDeep(future[0])],
            present: cloneDeep(future[0]),
            future: future.slice(1),
            appendCount,
            revision
          };
        case "CHANGE_SD":
          if (
            (present && present.id && past.length === 0) ||
            (action.value &&
              (action.value as IScreenDesign).id &&
              present &&
              present.id &&
              (action.value as IScreenDesign).id === (present as IScreenDesign).id)
          ) {
            // A remote collaborator's change must not become a local undo step (undoing it
            // would revert THEIR work): replace past[last] instead of appending, keeping
            // past[last] === present as the collab diff baseline invariant expects.
            const isRemoteCollabChange =
              action.changeReason === ESdChangeReason.RemoteCollabOp ||
              action.changeReason === ESdChangeReason.RemoteResync;
            if (isRemoteCollabChange && past.length > 0) {
              return {
                past: [...past.slice(0, past.length - 1), cloneDeep(action.value)],
                present: action.value,
                future: [],
                appendCount,
                revision
              };
            }
            const nextPast = [...past, cloneDeep(action.value)];
            return {
              past: nextPast.length < maxUndoCount + 2 ? nextPast : nextPast.slice(1),
              present: action.value,
              future: [],
              appendCount: appendCount + 1,
              revision
            };
          }
          // Seed `past` with the freshly loaded design so the first user edit is undoable.
          // Why: past acts as an inclusive log where past[last] === present; without this
          // seed, the first CHANGE_SD after load produces past.length === 1, making
          // canUndo() (past.length > 1) permanently false for the initial edit.
          return {
            past: action.value && (action.value as IScreenDesign).id ? [cloneDeep(action.value)] : [],
            present: action.value,
            future: [],
            appendCount,
            revision
          };
        case "MERGE_LAST": {
          // Folds the last `count` local entries into one, so a multi-step operation (deleting or
          // duplicating several components) undoes in one step. `past` is an inclusive log
          // (past[last] === present): keep the state before the operation, drop its intermediate
          // states, keep the final one. Entries the cap already evicted cannot be folded.
          const count: number = Math.min(action.count ?? 0, past.length - 1);
          if (count < 2) {
            return state;
          }
          return {
            past: [...past.slice(0, past.length - count), past[past.length - 1]],
            present,
            future,
            appendCount: appendCount - (count - 1),
            revision
          };
        }
        case "TERMINATE":
          return {
            past: [],
            present: {},
            future: [],
            appendCount,
            revision
          };
      }
    };
  };

  public static get = UndoableScreenDesign.undoable();
}
